import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { arApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Package, Building2, AlertTriangle, ChevronRight, Loader2, Factory, Calendar, Layers,
} from 'lucide-react';
import { cn } from '../lib/utils';

const STATUS_TONE = {
  'Order Received': 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-200',
  'Casting': 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  'On the Floor': 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
  'Hallmark / Stamping': 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300',
  'Ready for Dispatch': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  'Partially Dispatched': 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300',
};

const PRIORITY_TONE = {
  'High': 'bg-rose-100 text-rose-700 border-rose-200',
  'Medium': 'bg-amber-100 text-amber-700 border-amber-200',
  'Low': 'bg-slate-100 text-slate-600 border-slate-200',
};

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }
  catch { return d.slice(0, 10); }
}

const ActiveOrdersCard = ({ customerId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    arApi.customerActiveOrders(customerId)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId]);

  if (loading) {
    return (
      <Card data-testid="active-orders-card">
        <CardHeader className="pb-3"><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-12" /><Skeleton className="h-12" />
        </CardContent>
      </Card>
    );
  }

  const totalActive = data?.total || 0;
  const branchCount = data?.branch_count || 0;
  const groups = data?.groups || [];

  return (
    <Card data-testid="active-orders-card" className="overflow-hidden border-blue-200/60 dark:border-blue-500/30">
      <CardHeader className="pb-3 px-4 md:px-5 pt-4 bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-500/10 dark:to-sky-500/10 border-b">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base md:text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Active Orders
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {totalActive} active across {branchCount} {branchCount === 1 ? 'branch' : 'branches'} (incl. linked firms)
            </p>
          </div>
          <Badge variant="outline" className="text-xs font-semibold border-blue-300 text-blue-700 dark:text-blue-300">
            {totalActive} order{totalActive === 1 ? '' : 's'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {totalActive === 0 ? (
          <div className="text-center py-10 px-4">
            <Package className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">No active orders for this customer</p>
            {branchCount > 1 && (
              <p className="text-xs text-muted-foreground mt-1">Checked across {branchCount} linked firms.</p>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {groups.filter(g => g.orders.length > 0 || g.is_root).map(g => (
              <div key={g.customer_id || g.company_name} data-testid={`branch-${g.customer_id || g.company_name}`}>
                {/* Branch header — only show if there is more than one branch with orders */}
                {groups.filter(x => x.orders.length > 0).length > 1 && (
                  <div className="flex items-center justify-between gap-2 px-4 py-2 bg-slate-50/80 dark:bg-neutral-800/40 border-b sticky top-0 z-10">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 truncate">{g.company_name}</span>
                      {g.is_root && <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-amber-300 text-amber-700">Primary</Badge>}
                    </div>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0">{g.orders.length} order{g.orders.length === 1 ? '' : 's'}</span>
                  </div>
                )}
                {g.orders.length === 0 && g.is_root && groups.length === 1 ? null : g.orders.map(o => (
                  <button
                    key={o.id}
                    onClick={() => navigate(`/orders/${o.id}`)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-blue-50/40 dark:hover:bg-blue-500/5 transition-colors"
                    data-testid={`order-${o.id}`}
                  >
                    {/* Status pill */}
                    <div className="flex flex-col items-center gap-0.5 flex-shrink-0 w-20">
                      <span className={cn('text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded leading-tight text-center w-full', STATUS_TONE[o.status] || 'bg-slate-100 text-slate-600')}>
                        {o.status}
                      </span>
                      {o.priority && o.priority !== 'Medium' && (
                        <span className={cn('text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border w-full text-center', PRIORITY_TONE[o.priority] || PRIORITY_TONE.Medium)}>
                          {o.priority}
                        </span>
                      )}
                    </div>

                    {/* Identity */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-sm tabular-nums">{o.co_number || o.order_number}</span>
                        {o.co_number && o.order_number && o.co_number !== o.order_number && (
                          <span className="text-[10px] text-muted-foreground font-mono">· {o.order_number}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5 flex-wrap">
                        {o.factory_name && (
                          <span className="flex items-center gap-0.5"><Factory className="h-2.5 w-2.5" />{o.factory_name}</span>
                        )}
                        {o.diamond_quality && <span>{o.diamond_quality}</span>}
                        {o.gold_karat && <span>{o.gold_karat}</span>}
                      </div>
                    </div>

                    {/* Pieces */}
                    <div className="hidden sm:flex flex-col items-end flex-shrink-0 min-w-[70px]">
                      <span className="text-xs font-bold tabular-nums flex items-center gap-1">
                        <Layers className="h-3 w-3 text-muted-foreground" />
                        {o.ready_pieces}<span className="text-muted-foreground font-normal">/{o.pieces_ordered}</span>
                      </span>
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground">ready/total</span>
                    </div>

                    {/* Delivery */}
                    <div className="hidden md:flex flex-col items-end flex-shrink-0 min-w-[68px]">
                      <span className={cn('text-xs font-semibold flex items-center gap-1 tabular-nums',
                        o.days_to_delivery != null && o.days_to_delivery < 0 ? 'text-rose-600' :
                          o.days_to_delivery != null && o.days_to_delivery <= 7 ? 'text-amber-600' : 'text-slate-700 dark:text-slate-300')}>
                        <Calendar className="h-3 w-3" />
                        {fmtDate(o.delivery_date)}
                      </span>
                      {o.days_to_delivery != null && (
                        <span className={cn('text-[9px] uppercase tracking-wider',
                          o.days_to_delivery < 0 ? 'text-rose-600 font-bold' :
                            o.days_to_delivery <= 7 ? 'text-amber-600 font-bold' : 'text-muted-foreground')}>
                          {o.days_to_delivery < 0 ? `${Math.abs(o.days_to_delivery)}d late` :
                            o.days_to_delivery === 0 ? 'Today' : `${o.days_to_delivery}d left`}
                        </span>
                      )}
                    </div>

                    <ChevronRight className="h-4 w-4 text-muted-foreground/40 flex-shrink-0" />
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActiveOrdersCard;
